import {
  useCallback,
  useEffect,
  useRef,
  type MutableRefObject,
  type RefObject,
} from 'react';

import type { PanoramaHotspot } from '../contracts/models';
import {
  panoramaHotspotDomId,
  syncPannellumHotspots,
  type PanoramaHotspotElementRenderer,
  type PannellumViewer,
} from '../panorama/pannellum';

export interface UsePannellumHotspotsOptions {
  viewerRef: MutableRefObject<PannellumViewer | null>;
  containerRef: RefObject<HTMLElement>;
  hotspots: readonly PanoramaHotspot[];
  enabled?: boolean;
  className?: string;
  renderElement?: PanoramaHotspotElementRenderer;
  onHotspotClick?: (hotspot: PanoramaHotspot) => void;
  watchdogIntervalMs?: number;
}

export const usePannellumHotspots = ({
  viewerRef,
  containerRef,
  hotspots,
  enabled = true,
  className,
  renderElement,
  onHotspotClick,
  watchdogIntervalMs = 3000,
}: UsePannellumHotspotsOptions) => {
  const hotspotsRef = useRef(hotspots);
  hotspotsRef.current = hotspots;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const previousIdsRef = useRef<ReadonlySet<string>>(new Set());
  const clickRef = useRef(onHotspotClick);
  clickRef.current = onHotspotClick;
  const rendererRef = useRef(renderElement);
  rendererRef.current = renderElement;

  const syncHotspots = useCallback(
    (force = false) => {
      const viewer = viewerRef.current;
      if (!viewer) return;

      const currentHotspots = enabledRef.current ? hotspotsRef.current : [];
      const expectedIds = new Set(
        currentHotspots.map((hotspot) => panoramaHotspotDomId(hotspot.id)),
      );

      if (!force) {
        const mountedIds = new Set(
          Array.from(
            containerRef.current?.querySelectorAll<HTMLElement>(
              '[data-hs-id]',
            ) ?? [],
          ).map((element) => element.dataset.hsId ?? ''),
        );
        const isComplete =
          mountedIds.size === expectedIds.size &&
          [...expectedIds].every((id) => mountedIds.has(id));
        if (isComplete) return;
      }

      const result = syncPannellumHotspots(viewer, currentHotspots, {
        previousIds: previousIdsRef.current,
        className,
        renderElement: rendererRef.current,
        onClick: (hotspot) => clickRef.current?.(hotspot),
      });
      previousIdsRef.current = result.ids;
    },
    [className, containerRef, viewerRef],
  );

  useEffect(() => {
    syncHotspots(true);
  }, [enabled, hotspots, syncHotspots]);

  useEffect(() => {
    if (!enabled || watchdogIntervalMs <= 0) return;
    const intervalId = window.setInterval(
      () => syncHotspots(false),
      watchdogIntervalMs,
    );
    return () => window.clearInterval(intervalId);
  }, [enabled, syncHotspots, watchdogIntervalMs]);

  return { syncHotspots };
};
